import { useEffect, useRef, useState } from 'react'
import Icon from './Icon.jsx'
import VideoModal, { PlayIcon } from './VideoModal.jsx'
import Screenshot from './Screenshot.jsx'

const shotUrl = (name) => `${import.meta.env.BASE_URL}screenshots/${name}.webp`

// Screenshot carousel shared by the mobile app and venue page sections.
// `slides` items need { shot, title, text } and may carry a `url` for the browser frame
// and a `video` (YouTube id, or an array of ids played as a playlist) that adds a play button opening the video in a popup.
// Arrow keys, the buttons, the dots and a horizontal swipe on touch screens all change slide.
// The outgoing screenshot animates out while the new one comes in from the side you moved
// towards (both stacked in one grid cell), and the caption follows with a short stagger.
export default function Slideshow({ slides, phone = false, wide = false, noun = 'screen', labelPrefix = '', hotspots }) {
  const [{ i, dir, from }, setState] = useState({ i: 0, dir: 1, from: null })
  const [video, setVideo] = useState(null)
  const swipe = useRef(null)
  const n = slides.length
  const s = slides[i]

  // Both read the latest index inside the updater, so quick repeated clicks each count.
  const show = (j, d) => setState((cur) => (j === cur.i ? cur : { i: j, dir: d, from: cur.i }))
  const go = (d) => setState((cur) => ({ i: (cur.i + d + n) % n, dir: d, from: cur.i }))

  // Drop the outgoing layer once its animation is over. animationend usually does this first;
  // the timer covers background tabs and browsers that skip the event.
  useEffect(() => {
    if (from === null) return
    const t = setTimeout(() => setState((cur) => ({ ...cur, from: null })), 700)
    return () => clearTimeout(t)
  }, [from, i])

  // Warm the cache for both neighbours so the incoming screenshot is ready when it animates in.
  useEffect(() => {
    for (const j of [i + 1, i - 1]) new Image().src = shotUrl(slides[(j + n) % n].shot)
  }, [i, n, slides])

  const onPointerDown = (e) => { if (e.pointerType !== 'mouse') swipe.current = { x: e.clientX, y: e.clientY } }
  const onPointerUp = (e) => {
    const start = swipe.current
    swipe.current = null
    if (!start) return
    const dx = e.clientX - start.x
    // Mostly-horizontal drags of 40px+ count as a swipe; vertical ones are page scrolls.
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(e.clientY - start.y)) go(dx < 0 ? 1 : -1)
  }

  const side = dir > 0 ? 'next' : 'prev'
  // Clickable areas over the current screenshot (e.g. a venue page's tab bar) that jump to another slide.
  const spots = (x) => (hotspots ? hotspots(x) : []).map((h) => {
    const j = slides.findIndex((y) => y.shot === h.target)
    if (j < 0) return null
    return (
      <button key={h.label} className={`shot-hotspot ${j === i ? 'on' : ''}`} title={h.label} aria-label={`Show ${h.label}`}
        style={{ left: `${h.left}%`, width: `${h.width}%`, top: `${h.top}%`, height: `${h.height}%` }}
        onPointerDown={(e) => e.stopPropagation()} onClick={() => show(j, j > i ? 1 : -1)} />
    )
  })
  const frame = (x, live) => <Screenshot name={x.shot} label={`${labelPrefix}${x.title}`} url={x.url} phone={phone}>{live && spots(x)}</Screenshot>

  return (
    <div className={`app-slide ${wide ? 'wide' : ''}`} onKeyDown={(e) => { if (e.key === 'ArrowLeft') go(-1); if (e.key === 'ArrowRight') go(1) }}>
      <div className="app-slide-phone slide-stage" onPointerDown={onPointerDown} onPointerUp={onPointerUp} onPointerCancel={() => { swipe.current = null }}>
        {from !== null && (
          <div key={`out-${from}-${i}`} className={`slide-layer leave-${side}`} aria-hidden="true"
            onAnimationEnd={() => setState((cur) => ({ ...cur, from: null }))}>
            {frame(slides[from], false)}
          </div>
        )}
        <div key={`in-${i}`} className={`slide-layer ${from !== null ? `enter-${side}` : ''}`}>
          {frame(s, true)}
        </div>
      </div>
      <div className="app-slide-text" aria-live="polite">
        <div key={i} className={from !== null ? `slide-caption enter-${side}` : 'slide-caption'}>
          {s.video && (
            <button className="slide-play" onClick={() => setVideo(s.video)} aria-label={`Play video: ${s.title}`}>
              <PlayIcon />
            </button>
          )}
          <span className="slide-count">{String(i + 1).padStart(2, '0')} / {String(n).padStart(2, '0')}</span>
          <h3>{s.title}</h3>
          <p>{s.text}</p>
        </div>
        <div className="slide-nav">
          <button className="slide-btn" onClick={() => go(-1)} aria-label={`Previous ${noun}`}><Icon name="arrow" size={18} /></button>
          <button className="slide-btn" onClick={() => go(1)} aria-label={`Next ${noun}`}><Icon name="arrow" size={18} /></button>
          <div className="slide-dots">
            {slides.map((x, j) => (
              <button key={x.shot} className={j === i ? 'on' : ''} onClick={() => show(j, j > i ? 1 : -1)} aria-label={`Show ${x.title}`} />
            ))}
          </div>
        </div>
      </div>
      {video && <VideoModal video={video} onClose={() => setVideo(null)} />}
    </div>
  )
}
